import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { DashboardsContext } from '../../../../../contexts/DashboardsContext';
import { updateDashboardFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { Dashboard } from '../../../../../types/files';

const localStyles = makeStyles((theme) => ({
    syncExternally: {
        width: '18ch',
        padding: '2px 8px !important',
        height: 40,
        borderRadius: 8,
        color: '#0f766e',
        background: '#f0fdfa',
        border: `1.5px solid #99f6e4`,
        fontFamily: 'Lato',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
    },
    synced: {
        width: '18ch',
        padding: 'unset',
        "& > .MuiButton-startIcon": {
            paddingTop: 2,
        },
        "&.Mui-disabled": {
            color: '#059669',
            background: 'transparent',
            fontFamily: 'Lato',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            textTransform: 'none',
        },
    },
}));

const SyncStatusCell: React.FC<{ dashboard: Dashboard, flags?: any }> = ({ dashboard, flags }) => {
    const localClasses = localStyles();
    const { setDashboards } = useContext(DashboardsContext);
    const [loading, setLoading] = useState<string>('');
    const [syncExternally, setSyncExternally] = useState<boolean>(!!dashboard?.shouldSyncExternally);

    const handleSyncExternal = useCallback(async () => {
        const updatedDashboard = { ...dashboard, shouldSyncExternally: true };

        setLoading('sync');
        await updateDashboardFunc(updatedDashboard);
        setDashboards(prev => prev.map(d => d.id === updatedDashboard.id ? updatedDashboard : d));
        setSyncExternally(true);
        setLoading('');
    }, [dashboard, setDashboards]);

    useEffect(() => {
        setSyncExternally(!!dashboard?.shouldSyncExternally);
    }, [dashboard?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (<>
        {syncExternally ? (
            <Button variant="text"
                className={localClasses.synced}
                startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "18px !important", color: '#059669' }} />}
                disabled> {'Sync complete'} </Button>
        ) : (
            <Tooltip title={!flags?.zapierPlan ? 'Upgrade your plan to sync externally' : ''} placement="top">
                <span>
                    <Button variant="outlined"
                        className={localClasses.syncExternally}
                        disabled={!flags?.zapierPlan}
                        startIcon={(loading === 'sync') ? (
                            <CircularProgress size={24} sx={{ color: theme.palette.primary.light }} />
                        ): (<CachedIcon sx={{ fontSize: "18px !important", color: '#0f766e' }} />)}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleSyncExternal();
                        }} disableRipple>{'Sync externally'} </Button>
                </span>
            </Tooltip>
        )}
    </>);
}

export default memo(withLDConsumer()(SyncStatusCell));
